import { Component, inject, signal, computed, effect, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import {
  SupervisionService, SupervisionSnapshot, SupervisionAction, FluxEntry
} from '../../core/services/supervision.service';
import { SessionService } from '../../core/session.service';
import { ToastService } from '../../core/services/toast.service';
import { EquipementService, Equipement } from '../../core/services/equipement.service';
import { SensorHistoricalService, SensorHistoricalPoint } from '../../core/services/sensor-historical.service';
import { TimezoneService } from '../../core/services/timezone.service';
import { Subscription, interval, combineLatest } from 'rxjs';
import { switchMap, startWith } from 'rxjs/operators';
import { PilotageService, PilotageEstablishment } from '../../core/services/pilotage.service';
import { PageSiteSwitcherComponent } from '../../component/page-site-switcher/page-site-switcher.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-supervision',
  standalone: true,
  imports: [CommonModule, DecimalPipe, PageSiteSwitcherComponent, TranslatePipe],
  templateUrl: './supervision.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./supervision.component.scss'],
})
export class SupervisionComponent implements OnDestroy {
  private readonly api             = inject(SupervisionService);
  private readonly session         = inject(SessionService);
  private readonly toast           = inject(ToastService);
  private readonly equipService    = inject(EquipementService);
  private readonly sensorHistSvc   = inject(SensorHistoricalService);
  private readonly tzService       = inject(TimezoneService);
  private readonly pilotageService = inject(PilotageService);
  private readonly translate       = inject(TranslateService);

  data          = signal<SupervisionSnapshot | null>(null);
  loading       = signal(true);
  actionLoading = signal<string | null>(null);

  // Tick chaque seconde — tous les affichages temporels en dépendent
  private readonly tick               = signal(Date.now());
  private readonly lastFrameTimestamp = signal(0);

  readonly currentTime = computed(() =>
    new Date(this.tick()).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
  );

  readonly lastFrameTimeStr = computed(() => {
    const ts = this.lastFrameTimestamp();
    return ts > 0
      ? new Date(ts).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
      : '—';
  });

  readonly liveDelay = computed(() => {
    const ts = this.lastFrameTimestamp();
    if (ts === 0) return '—';
    const diffMin = Math.round((this.tick() - ts) / 60_000);
    return diffMin < 1 ? '< 1 min' : `${diffMin} min`;
  });

  readonly frameAgeClass = computed(() => {
    const ts = this.lastFrameTimestamp();
    if (ts === 0) return 'frame-age-unknown';
    const diffMin = (this.tick() - ts) / 60_000;
    if (diffMin < 10) return 'frame-age-ok';
    if (diffMin < 30) return 'frame-age-warn';
    return 'frame-age-stale';
  });

  private timeInterval?: ReturnType<typeof setInterval>;
  private pollSub?: Subscription;
  private historicalPoints: SensorHistoricalPoint[] = [];
  private currentEstId = '';

  constructor() {
    this.updateTime();
    this.timeInterval = setInterval(() => this.updateTime(), 1000);

    effect(() => {
      const id = this.session.selectedId();
      if (id && id !== this.currentEstId) {
        this.currentEstId = id;
        this.startPolling(id);
      }
    }, { allowSignalWrites: true });
  }

  ngOnDestroy() {
    clearInterval(this.timeInterval);
    this.pollSub?.unsubscribe();
  }

  // ─── Polling ────────────────────────────────────────────────────────────────

  private startPolling(id: string) {
    this.pollSub?.unsubscribe();
    this.loading.set(true);
    this.data.set(null);

    const dayOfWeek = this.tzService.getDateInEstablishmentTimezone().getDay();

    // Chargement des moyennes historiques (une fois par établissement/jour)
    this.sensorHistSvc.getSensorHistoricalByEstablishment(id, dayOfWeek)
      .subscribe({ next: r => { this.historicalPoints = r.points; } });

    // Polling toutes les 60 s : pilotage (totaux journaliers) + équipements (dernière trame live)
    this.pollSub = interval(5 * 60_000).pipe(
      startWith(0),
      switchMap(() => combineLatest([
        this.pilotageService.getEstablishments(),
        this.equipService.getSnapshot(id),
      ]))
    ).subscribe({
      next: ([establishments, equipSnap]) => {
        const est     = establishments.find(e => e.establishmentId === id) ?? null;
        const sensors = equipSnap.equipements.filter(e => e.active);
        this.rebuildFromData(est, sensors);
        this.loading.set(false);
      },
      error: () => this.loading.set(false),
    });
  }

  // ─── Construction du snapshot ────────────────────────────────────────────────

  private rebuildFromData(est: PilotageEstablishment | null, sensors: Equipement[]) {
    // Présence = SUM(periodic_in) − SUM(periodic_out) depuis 06h00 UTC (calculé par le backend)
    const patientsPresents = est?.currentOccupancy ?? 0;

    // Flux du dernier créneau LoRaWAN (~30 min) — dernière trame live par capteur
    const periodicIn  = sensors.reduce((sum, s) => sum + (s.periodicIn  ?? 0), 0);
    const periodicOut = sensors.reduce((sum, s) => sum + (s.periodicOut ?? 0), 0);
    const delta       = periodicIn - periodicOut;

    // Tension dérivée du temps de prise en charge (waitMinutes vs seuils configurés) —
    // seul indicateur retenu, jamais de l'occupation brute (voir /pilotage).
    const tension: 'stable' | 'modérée' | 'critique' =
      est?.waitMinutes == null ? 'stable' :
      est.waitMinutes > (est.thresholdHigh ?? 20) ? 'critique' :
      est.waitMinutes > (est.thresholdLow  ?? 10) ? 'modérée'  : 'stable';

    // Charge/capacité : présence vs seuil critique configuré sur le capteur
    const thresholdHigh = est?.thresholdHigh ?? 20;
    const chargeCapacite = thresholdHigh > 0
      ? Math.min(200, Math.round((patientsPresents / thresholdHigh) * 100))
      : 100;

    const tendance = delta > 0
      ? this.translate.instant('pilotage.supervision.flux.trendValue', { delta: `+${delta}` })
      : delta < 0 ? this.translate.instant('pilotage.supervision.flux.trendValue', { delta }) : this.translate.instant('pilotage.supervision.flux.trendStable');

    const alertLevel: 'vert' | 'orange' | 'rouge' =
      tension === 'critique' ? 'rouge' : tension === 'modérée' ? 'orange' : 'vert';

    this.updateLiveDelay(sensors);

    const prev = this.data();

    this.data.set({
      header: {
        patientsPresents,
        enAttente:        patientsPresents,
        evolutionAttente: delta,
        tempsAttenteMoyen: 0,
        chargeCapacite,
        tension,
        satisfaction: prev?.header.satisfaction ?? 0,
        debitGlobal:  periodicOut,
        tempsCyclePatient: 0,
      },
      flux: {
        entries:       this.buildFluxEntries(periodicIn, periodicOut),
        accumulation:  delta > 0 ? 30 : 0,
        tendance,
      },
      production: prev?.production ?? { salles: [], performanceStaff: 0, tendanceStaff: 0 },
      prediction: {
        tempsAvantDebordement: null,
        saturationEstimee:     chargeCapacite,
        projectionAttente:     0,
        picAVenir:             '',
        tempsRetourNormal:     null,
      },
      alert: {
        level:               alertLevel,
        titre:               tension === 'critique' ? this.translate.instant('pilotage.supervision.alert.saturationInProgress')
                           : tension === 'modérée'  ? this.translate.instant('pilotage.supervision.alert.flowRising')
                           : this.translate.instant('pilotage.supervision.alert.flowNormal'),
        diagnostic:          periodicIn > 0 || periodicOut > 0
                           ? this.translate.instant('pilotage.supervision.alert.diagnosticFlow', { in: periodicIn, out: periodicOut })
                           : this.translate.instant('pilotage.supervision.alert.diagnosticNoData'),
        tendanceFile:        delta > 0 ? this.translate.instant('pilotage.supervision.alert.queueRising', { delta })
                           : delta < 0 ? this.translate.instant('pilotage.supervision.alert.queueShrinking') : this.translate.instant('pilotage.supervision.alert.queueStable'),
        debordementEstime:   null,
        actionRecommandee:   '',
        depuis:              this.liveDelay(),
      },
      actions: this.buildActions(tension, delta, patientsPresents),
    });
  }

  /**
   * Construit 8 tranches de 15 min (= 2h).
   * - 2 tranches les plus récentes  → données live (periodicIn/Out ÷ 2)
   * - Tranches antérieures          → moyennes historiques (avgIn/Out ÷ 2)
   */
  private buildFluxEntries(periodicIn: number, periodicOut: number): FluxEntry[] {
    const now     = new Date();
    const entries: FluxEntry[] = [];

    for (let i = 7; i >= 0; i--) {
      const t = new Date(now.getTime() - i * 15 * 60_000);
      const h = String(t.getHours()).padStart(2, '0');
      const m = String(Math.floor(t.getMinutes() / 15) * 15).padStart(2, '0');

      let entrees: number;
      let sorties: number;

      if (i < 2) {
        // Créneau en cours (30 min LoRaWAN) divisé en 2 slots de 15 min
        entrees = Math.round(periodicIn  / 2);
        sorties = Math.round(periodicOut / 2);
      } else {
        // Créneaux passés : moyennes historiques du jour de la semaine
        const totalMin  = t.getHours() * 60 + Math.floor(t.getMinutes() / 30) * 30;
        const slotIndex = Math.floor((totalMin - 6 * 60) / 30);

        if (slotIndex >= 0 && slotIndex < this.historicalPoints.length) {
          const pt = this.historicalPoints[slotIndex];
          entrees = Math.round(pt.avgIn  / 2);
          sorties = Math.round(pt.avgOut / 2);
        } else {
          entrees = 0;
          sorties = 0;
        }
      }

      entries.push({ time: `${h}:${m}`, entrees, sorties, capaciteMax: 4 });
    }

    return entries;
  }

  private buildActions(
    tension: string, delta: number, presence: number
  ): SupervisionAction[] {
    if (delta > 0 && presence > 12) {
      return [
        { id: '1', label: this.translate.instant('pilotage.supervision.actions.openRoom'),  icon: '🧪', impact: this.translate.instant('pilotage.supervision.actions.increaseCapacity'), type: 'ouvrir-salle',    prioritaire: true },
        { id: '2', label: this.translate.instant('pilotage.supervision.actions.reinforceFrontDesk'), icon: '👥', impact: this.translate.instant('pilotage.supervision.actions.speedUpCheckin'), type: 'envoyer-renfort', prioritaire: false },
      ];
    }
    if (delta > 0) {
      return [{ id: '1', label: this.translate.instant('pilotage.supervision.actions.reinforceFrontDesk'), icon: '👥', impact: this.translate.instant('pilotage.supervision.actions.reduceBacklog'), type: 'envoyer-renfort', prioritaire: true }];
    }
    if (delta < 0 && presence > 4) {
      return [{ id: '1', label: this.translate.instant('pilotage.supervision.actions.reduceFrontDesk'), icon: '👥', impact: this.translate.instant('pilotage.supervision.actions.optimizeResources'), type: 'alleger-accueil', prioritaire: true }];
    }
    return [];
  }

  private updateLiveDelay(sensors: Equipement[]) {
    const mostRecent = sensors
      .map(s => s.derniereTrameAt ? new Date(s.derniereTrameAt).getTime() : 0)
      .filter(t => t > 0)
      .sort((a, b) => b - a)[0];

    this.lastFrameTimestamp.set(mostRecent ?? 0);
  }

  private updateTime() {
    this.tick.set(Date.now());
  }

  // ─── Getters template ───────────────────────────────────────────────────────

  get establishmentName(): string {
    const id = this.session.selectedId();
    if (!id) return '';
    return this.session.establishments().find(e => e.id === id)?.name ?? '';
  }

  get tensionClass(): string {
    const t = this.data()?.header.tension;
    return t === 'critique' ? 'critique' : t === 'modérée' ? 'moderee' : 'stable';
  }

  get tensionLabel(): string {
    const t = this.data()?.header.tension;
    const key = t === 'critique' ? 'pilotage.supervision.status.high' : t === 'modérée' ? 'pilotage.supervision.status.moderate' : 'pilotage.supervision.status.fluid';
    return this.translate.instant(key);
  }

  get chargeClass(): string {
    const c = this.data()?.header.chargeCapacite ?? 0;
    return c > 100 ? 'critique' : c > 80 ? 'moderee' : 'stable';
  }

  get chargeGaugeWidth(): number {
    return Math.min(100, this.data()?.header.chargeCapacite ?? 0);
  }

  get alertClass(): string {
    const l = this.data()?.alert.level;
    return l === 'rouge' ? 'alert-rouge' : l === 'orange' ? 'alert-orange' : 'alert-vert';
  }

  get primaryAction(): SupervisionAction | null {
    return this.data()?.actions.find(a => a.prioritaire) ?? null;
  }

  get secondaryActions(): SupervisionAction[] {
    return this.data()?.actions.filter(a => !a.prioritaire) ?? [];
  }

  getFluxBarHeight(value: number): number {
    const max = Math.max(...(this.data()?.flux.entries.map(e => Math.max(e.entrees, e.sorties, e.capaciteMax)) ?? [1]));
    return (value / max) * 100;
  }

  getCapacityBottom(value: number): number {
    const max = Math.max(...(this.data()?.flux.entries.map(e => Math.max(e.entrees, e.sorties, e.capaciteMax)) ?? [1]));
    return (value / max) * 100;
  }

  getOccupationClass(v: number): string {
    return v >= 90 ? 'occ-critique' : v >= 70 ? 'occ-moderee' : 'occ-ok';
  }

  getCapaciteActive(): number {
    const salles = this.data()?.production.salles ?? [];
    return salles.filter(s => s.debit > 0).reduce((acc, s) => acc + s.debit / 4, 0);
  }

  getAttenteEstimee(): number {
    const d = this.data();
    if (!d) return 0;
    const capacite = this.getCapaciteActive();
    if (capacite <= 0) return 0;
    return Math.round((d.header.patientsPresents / capacite) * 15);
  }

  getTempsRetourFluide(): number {
    const d = this.data();
    if (!d) return 0;
    const capacite = this.getCapaciteActive();
    const presence = d.header.patientsPresents;
    const tempsPassage = capacite > 0 ? (presence / capacite) * 15 : 0;
    let seuilInferieur: number;
    if (tempsPassage > 30) {
      seuilInferieur = Math.round(capacite * 30 / 15);
    } else if (tempsPassage >= 10) {
      seuilInferieur = Math.round(capacite * 10 / 15);
    } else {
      return 0;
    }
    const charge = d.header.chargeCapacite / 100;
    const delta  = capacite - (charge * capacite);
    if (delta <= 0) return 0;
    const patientsAResorber = Math.max(0, presence - seuilInferieur);
    return Math.max(1, Math.round((patientsAResorber / delta) * 15));
  }

  getRisqueDegradation(): number {
    const d = this.data();
    if (!d) return 0;
    const capacite = this.getCapaciteActive();
    const presence = d.header.patientsPresents;
    const tempsPassage = capacite > 0 ? (presence / capacite) * 15 : 0;
    let seuilSuperieur: number;
    if (tempsPassage < 10)       seuilSuperieur = Math.round(capacite * 10 / 15);
    else if (tempsPassage <= 30) seuilSuperieur = Math.round(capacite * 30 / 15);
    else                         seuilSuperieur = Math.round(capacite * 60 / 15);
    if (seuilSuperieur <= 0) return 100;
    return Math.min(100, Math.max(0, Math.round((presence / seuilSuperieur) * 100)));
  }

  getTempsClass(t: number): string {
    return t > 8 ? 'temps-lent' : t > 6 ? 'temps-moyen' : 'temps-ok';
  }

  getAccumulationDuree(): number {
    return this.data()?.flux.accumulation ?? 0;
  }

  getLastDelta(): number {
    const entries = this.data()?.flux.entries ?? [];
    if (!entries.length) return 0;
    const last = entries[entries.length - 1];
    return last.entrees - last.sorties;
  }

  getCumulAt(index: number): number {
    const entries = this.data()?.flux.entries ?? [];
    let cumul = 0;
    for (let i = 0; i <= index; i++) cumul += entries[i].entrees - entries[i].sorties;
    return cumul;
  }

  getTotalCumul(): number {
    return (this.data()?.flux.entries ?? []).reduce((acc, e) => acc + (e.entrees - e.sorties), 0);
  }

  getEndTime(startTime: string): string {
    const [h, m] = startTime.split(':').map(Number);
    const totalMin = h * 60 + m + 15;
    return `${String(Math.floor(totalMin / 60) % 24).padStart(2, '0')}:${String(totalMin % 60).padStart(2, '0')}`;
  }

  getAlertesActives(): { message: string; color: string; status: string; statusLabel: string }[] {
    const d = this.data();
    if (!d) return [];
    const alertes: { message: string; color: string; status: string; statusLabel: string }[] = [];

    const capacite = this.getCapaciteActive();
    const lambda   = d.header.chargeCapacite / 100 * capacite;
    const delta    = lambda - capacite;

    if (d.header.chargeCapacite > 100)
      alertes.push({ message: this.translate.instant('pilotage.supervision.alertsList.highTraffic', { load: d.header.chargeCapacite }), color: 'dot-rouge', status: 'en-cours', statusLabel: this.translate.instant('pilotage.supervision.alertsList.statusOngoing') });

    const tempsPassage = this.getAttenteEstimee();
    if (tempsPassage > 20)
      alertes.push({ message: this.translate.instant('pilotage.supervision.alertsList.handlingTimeAbove20'), color: 'dot-rouge', status: 'en-cours', statusLabel: this.translate.instant('pilotage.supervision.alertsList.statusOngoing') });

    const salleAnomalie = d.production.salles.find(s => s.anomalie);
    if (salleAnomalie)
      alertes.push({ message: this.translate.instant('pilotage.supervision.alertsList.roomSlowdown', { room: salleAnomalie.nom }), color: 'dot-orange', status: 'en-cours', statusLabel: this.translate.instant('pilotage.supervision.alertsList.statusOngoing') });

    if (delta < 0 && d.header.patientsPresents > 4)
      alertes.push({ message: this.translate.instant('pilotage.supervision.alertsList.decreasingFlowCloseRoom'), color: 'dot-vert', status: 'recommande', statusLabel: this.translate.instant('pilotage.supervision.alertsList.statusRecommended') });

    if (delta < 0 && d.header.patientsPresents <= 4)
      alertes.push({ message: this.translate.instant('pilotage.supervision.alertsList.smoothReturnReduceFrontDesk'), color: 'dot-vert', status: 'recommande', statusLabel: this.translate.instant('pilotage.supervision.alertsList.statusRecommended') });

    if (alertes.length === 0)
      alertes.push({ message: this.translate.instant('pilotage.supervision.alertsList.noActiveAlert'), color: 'dot-vert', status: 'info', statusLabel: this.translate.instant('pilotage.supervision.alertsList.statusOk') });

    return alertes;
  }

  executeAction(type: string | undefined) {
    const id = this.session.selectedId();
    if (!id || !type) return;
    this.actionLoading.set(type);
    this.api.executeAction(id, type).subscribe({
      next: (r) => { this.toast.show(r.message, 'success'); this.actionLoading.set(null); },
      error: () => { this.toast.show(this.translate.instant('pilotage.supervision.toast.errorTitle'), 'error'); this.actionLoading.set(null); },
    });
  }
}
